// src/api/function_data.js

import { Auth } from "shared/auth";

export const BASE_URL = "https://1ftxw98ih3.execute-api.eu-west-1.amazonaws.com/cia_pulse_1";

/**
 * Centralized auth header builder.
 * Checks authentication status, retrieves the Bearer token, and returns headers.
 * @param {boolean} includeContentType - Whether to include Content-Type: application/json
 * @returns {Object} Headers object with Authorization (and optionally Content-Type)
 */
export function getAuthHeaders(includeContentType = true) {
  if (!Auth.isAuthenticated()) {
    Auth.login();
    throw new Error("Not authenticated. Redirecting to login.");
  }

  const token = Auth.getToken();
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
}

/**
 * Centralized response handler for 401 (unauthorized) responses.
 * Clears tokens and redirects to login on 401, otherwise returns response as-is.
 * @param {Response} response - The fetch Response object
 * @returns {Response} The original response if not 401
 */
export function handleResponse(response) {
  if (response.status === 401) {
    localStorage.removeItem("id_token");
    localStorage.removeItem("access_token");
    Auth.login();
    throw new Error("Session expired. Redirecting to login.");
  }
  return response;
}

export const getfunctionConfig = async (function_id, week, year) => {
  try {
    const response = await fetch(
      `${BASE_URL}/getconfig?function_id=${function_id}&year=${year}&week=${week}`, {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );
    handleResponse(response);
    if (!response.ok) throw new Error("Failed to fetch config");
    
    const result = await response.json();
    
    // Return only the data part (week_data)
    return result.data;
    
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

export const updateFunctionConfig = async (payload) => {
  try {
    const response = await fetch(`${BASE_URL}/update_function_config`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    handleResponse(response);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to update configuration");
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating config:", error);
    throw error;
  }
};

export const getUserPermissions = async (login, function_id, year) => {
  try {
    const response = await fetch(
      `${BASE_URL}/get_permissions?login=${encodeURIComponent(login)}&function_id=${encodeURIComponent(function_id)}&year=${encodeURIComponent(year)}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );
    handleResponse(response);
    if (!response.ok) throw new Error("Failed to fetch permissions");
    return await response.json();
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

export const getAccessList = async (function_id,year) => {
  try {
    const response = await fetch(
      `${BASE_URL}/get_access_list?function_id=${encodeURIComponent(function_id)}&year=${year}`,{
        method: "GET",
        headers: getAuthHeaders(),
      }
    );
    handleResponse(response);
    if (!response.ok) throw new Error("Failed to fetch access list");
    return await response.json();
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

export const saveAccessList = async (function_id, access_list,year) => {
  try {
    const response = await fetch(`${BASE_URL}/save_access_list`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ function_id, access_list,year }),
    });
    handleResponse(response);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Failed to save access list");
    }
    return await response.json();
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

export const getReportPdf = async (function_id, itemKey, week, year) => {
  try {
    const response = await fetch(
      `${BASE_URL}/get_report_pdf?function_id=${encodeURIComponent(function_id)}&key=${encodeURIComponent(itemKey)}&week=${encodeURIComponent(week)}&year=${encodeURIComponent(year)}`,
       { headers: getAuthHeaders() }
    );
    handleResponse(response);

    if (response.status === 404) {
      throw new Error("PDF not found for this week/year");
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || "Failed to fetch PDF report");
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    return url;
  } catch (error) {
    console.error("Error fetching report PDF:", error);
    throw error;
  }
};

/**
 * Same as getReportPdf but returns the raw Blob instead of an object URL.
 * Returns null on 404 (no report for that week/key). Used by the cached
 * ReportPage query — the component owns the object-URL lifecycle so cached
 * blobs don't leak.
 */
export const getReportPdfBlob = async (function_id, itemKey, week, year) => {
  const response = await fetch(
    `${BASE_URL}/get_report_pdf?function_id=${encodeURIComponent(function_id)}&key=${encodeURIComponent(itemKey)}&week=${encodeURIComponent(week)}&year=${encodeURIComponent(year)}`,
    { headers: getAuthHeaders() }
  );
  handleResponse(response);

  if (response.status === 404) {
    return null; // no report uploaded yet for this week/key
  }
  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || "Failed to fetch PDF report");
  }

  return await response.blob();
};

export const getReportCallout = async (function_id, itemKey, week, year) => {
  try {
    const response = await fetch(
      `${BASE_URL}/get_report_callout?function_id=${encodeURIComponent(function_id)}&key=${encodeURIComponent(itemKey)}&week=${encodeURIComponent(week)}&year=${encodeURIComponent(year)}`,
       { headers: getAuthHeaders() }
    );
    handleResponse(response);

    if (response.status === 404) {
      return null; // no callout exists yet
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || "Failed to fetch callout");
    }

    const data = await response.json();
    return data.callout; // joined string ready for <pre> display
  } catch (error) {
    console.error("Error fetching report callout:", error);
    throw error;
  }
};

// ═══════════════════════════════════════════
// Q&A
// ═══════════════════════════════════════════

export const getQuestions = async (function_id, itemKey, week, year) => {
  try {
    const params = new URLSearchParams({
      function_id,
      key: itemKey,
      week: String(week),
      year: String(year),
    });
    const response = await fetch(`${BASE_URL}/get_questions?${params}`, {
      headers: getAuthHeaders(),
    });
    handleResponse(response);
    if (!response.ok) throw new Error("Failed to fetch questions");
    return await response.json();
  } catch (error) {
    console.error("Error fetching questions:", error);
    throw error;
  }
};

export const postQuestion = async (
  function_id,
  itemKey,
  week,
  year,
  author,
  question
) => {
  try {
    const response = await fetch(`${BASE_URL}/post_question`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        function_id,
        key: itemKey,
        week: String(week),
        year: String(year),
        author,
        question,
      }),
    });
    handleResponse(response);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Failed to post question");
    }
    return await response.json();
  } catch (error) {
    console.error("Error posting question:", error);
    throw error;
  }
};

export const postAnswer = async (
  questionId,
  author,
  text,
  function_id,
  itemKey,
  week,
  year
) => {
  try {
    const response = await fetch(`${BASE_URL}/post_answer`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        question_id: questionId,
        author,
        text,
        function_id,
        key: itemKey,
        week: String(week),
        year: String(year),
      }),
    });
    handleResponse(response);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Failed to post answer");
    }
    return await response.json();
  } catch (error) {
    console.error("Error posting answer:", error);
    throw error;
  }
};


export const getFunctionOptions = async () => {
  try {
    const response = await fetch(`${BASE_URL}/get_functions_list`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    handleResponse(response);
    if (!response.ok) throw new Error("Failed to fetch function options");
    return await response.json();
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
};

/**
 * POST /onboard
 * Onboards a new function: registers it in the global registry, seeds admin
 * access, and creates the S3 folder skeleton. Uses the standard Cognito auth
 * path (same authorizer as every other endpoint).
 */
export const onboardFunction = async (payload) => {
  try {
    const response = await fetch(`${BASE_URL}/onboard`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    handleResponse(response);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Onboarding failed (${response.status})`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error onboarding function:", error);
    throw error;
  }
};



export const getActionTracker = async (function_id, year) => {
  try {
    const response = await fetch(
      `${BASE_URL}/get_action_tracker?function_id=${encodeURIComponent(
        function_id
      )}&year=${encodeURIComponent(year)}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );
    handleResponse(response);
    if (!response.ok) {
      if (response.status === 404) return { actions: [] };
      throw new Error("Failed to fetch action tracker");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching action tracker:", error);
    throw error;
  }
};

export const saveActionTracker = async (function_id, year, actions) => {
  try {
    const response = await fetch(`${BASE_URL}/save_action_tracker`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ function_id, year, actions }),
    });
    handleResponse(response);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Failed to save action tracker");
    }
    return await response.json();
  } catch (error) {
    console.error("Error saving action tracker:", error);
    throw error;
  }
};


export const getBusinessUpdates = async (function_id, year, week, key) => {
  try {
    const response = await fetch(
      `${BASE_URL}/get_business_updates?function_id=${encodeURIComponent(
        function_id
      )}&year=${encodeURIComponent(year)}&week=${encodeURIComponent(
        week
      )}&key=${encodeURIComponent(key)}`,
      {
        method: "GET",
        headers: getAuthHeaders(),
      }
    );
    handleResponse(response);
    if (!response.ok) {
      if (response.status === 404) return { segments: [] };
      throw new Error("Failed to fetch business updates");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching business updates:", error);
    throw error;
  }
};

export const saveBusinessUpdates = async (
  function_id,
  year,
  week,
  key,
  segments
) => {
  try {
    const response = await fetch(`${BASE_URL}/save_business_updates`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ function_id, year, week, key, segments }),
    });
    handleResponse(response);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || "Failed to save business updates");
    }
    return await response.json();
  } catch (error) {
    console.error("Error saving business updates:", error);
    throw error;
  }
};


export async function getAllBusinessUpdates(functionId, year, week) {
  const params = new URLSearchParams({
    function_id: functionId,
    year: String(year),
    week: String(week),
  });

  const response = await fetch(
    `${BASE_URL}/get_all_business_updates?${params.toString()}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );
  handleResponse(response);

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      errorBody.error || `Failed to fetch business updates (${response.status})`
    );
  }

  const result = await response.json();

  // If the body came back as a string (API Gateway proxy), parse it
  if (typeof result.body === "string") {
    return JSON.parse(result.body);
  }

  return result;
}


export async function getCallouts(functionId, year, week, key) {
  const params = new URLSearchParams({
    function_id: functionId,
    year: String(year),
    week: String(week),
    key,
  });

  const response = await fetch(`${BASE_URL}/get_callouts?${params.toString()}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  handleResponse(response);

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Failed to fetch callouts (${response.status})`);
  }

  return response.json();
}

/* ═══════════════════════════════════════════
   CALLOUT: SAVE
   ═══════════════════════════════════════════ */

export async function saveCallouts(functionId, year, week, key, callouts) {
  const response = await fetch(`${BASE_URL}/save_callouts`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      function_id: functionId,
      year: String(year),
      week: String(week),
      key,
      callouts,
    }),
  });
  handleResponse(response);

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error || `Failed to save callouts (${response.status})`);
  }

  return response.json();
}


/* ═══════════════════════════════════════════
   FILE UPLOAD: POST via FormData (multipart)
   ═══════════════════════════════════════════ */

export async function uploadFile(functionId, year, week, key, file) {
  const formData = new FormData();
  formData.append("function_id", functionId);
  formData.append("year", String(year));
  formData.append("week", String(week));
  formData.append("key", key);
  formData.append("pdf_file", file);  // actual File object — browser handles encoding

  const response = await fetch(`${BASE_URL}/upload_file`, {
    method: "POST",
    headers: getAuthHeaders(false),
    body: formData,
  });
  handleResponse(response);

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.detail || errData.error || `Upload failed (${response.status})`);
  }

  return response.json();
}

export async function getReportStatus(functionId, year, week, keys) {
  if (!keys || keys.length === 0) return {};

  const params = new URLSearchParams({
    function_id: functionId,
    year: String(year),
    week: String(week),
    keys: keys.join(","),
  });

  const response = await fetch(
    `${BASE_URL}/check_report_status?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  handleResponse(response);

  if (!response.ok) {
    throw new Error(`Failed to fetch report status: ${response.statusText}`);
  }

  const data = await response.json();
  return data.status || {};
}



// Add these to your existing function_data.js

/**
 * POST /get_all_report_pdfs
 * Sends all keys at once, gets back presigned S3 URLs for each PDF.
 * Returns: { results: { [key]: { status, url, error? } }, total_found, ... }
 */
export const getAllReportPdfs = async (function_id, keys, week, year) => {
  try {
    const response = await fetch(`${BASE_URL}/get_all_report_pdfs`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        function_id,
        keys,
        week,
        year,
      }),
    });
    handleResponse(response);

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || "Failed to fetch report PDFs");
    }

    const data = await response.json();
    return data.results; // { ipi: { status, url }, bar: { status, url }, ... }
  } catch (error) {
    console.error("Error fetching all report PDFs:", error);
    throw error;
  }
};

/**
 * POST /get_all_report_callouts
 * Sends all keys at once, backend reads callout.json ONCE and extracts all.
 * Returns: { results: { [key]: { status, callout, error? } }, ... }
 */
export const getAllReportCallouts = async (function_id, keys, week, year) => {
  try {
    const response = await fetch(`${BASE_URL}/get_all_report_callouts`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        function_id,
        keys,
        week,
        year,
      }),
    });
    handleResponse(response);

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || "Failed to fetch report callouts");
    }

    const data = await response.json();
    return data.results; // { ipi: { status, callout }, bar: { status, callout }, ... }
  } catch (error) {
    console.error("Error fetching all report callouts:", error);
    throw error;
  }
};


// ═══════════════════════════════════════════
// MEETING ROLES: BULK OPERATIONS (1 API call instead of 52)
// ═══════════════════════════════════════════

/**
 * GET /get_all_meeting_roles?function_id=xxx&year=2026
 * Returns meeting roles for ALL weeks in a single API call.
 * Response: { roles: { "1": { meeting_coordination, meeting_facilitator, meeting_note_taker }, ... } }
 */
export const getAllMeetingRoles = async (functionId, year) => {
  try {
    const params = new URLSearchParams({
      function_id: functionId,
      year: String(year),
    });
    const response = await fetch(`${BASE_URL}/get_all_meeting_roles?${params.toString()}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    handleResponse(response);
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || "Failed to fetch meeting roles");
    }
    const data = await response.json();
    return data.roles || {};
  } catch (error) {
    console.error("Error fetching all meeting roles:", error);
    throw error;
  }
};

/**
 * POST /bulk_update_meeting_roles
 * Updates meeting roles for multiple weeks in a SINGLE API call.
 * Body: { function_id, year, updates: { "28": { meeting_coordination: {...}, ... }, "29": {...} } }
 */
export const bulkUpdateMeetingRoles = async (functionId, year, updates) => {
  try {
    const response = await fetch(`${BASE_URL}/bulk_update_meeting_roles`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        function_id: functionId,
        year: String(year),
        updates,
      }),
    });
    handleResponse(response);
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || "Failed to update meeting roles");
    }
    return await response.json();
  } catch (error) {
    console.error("Error bulk updating meeting roles:", error);
    throw error;
  }
};


// ═══════════════════════════════════════════
// WBR PACK: Single bulk endpoint for all data
// ═══════════════════════════════════════════

/**
 * POST /get_wbr_pack_data
 * Returns ALL data needed for WBR Pack in a SINGLE API call:
 * - report_pdfs (presigned URLs)
 * - callouts
 * - business_updates
 * - action_tracker
 */
export const getWbrPackData = async (functionId, year, week, reportKeys) => {
  try {
    const response = await fetch(`${BASE_URL}/get_wbr_pack_data`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        function_id: functionId,
        year: String(year),
        week: String(week),
        report_keys: reportKeys,
      }),
    });
    handleResponse(response);
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || "Failed to fetch WBR pack data");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching WBR pack data:", error);
    throw error;
  }
};


// ═══════════════════════════════════════════
// AI INSIGHTS
// ═══════════════════════════════════════════

export const getAiPrompts = async (functionId, year) => {
  const params = new URLSearchParams({ function_id: functionId, year: String(year) });
  const response = await fetch(`${BASE_URL}/get_ai_prompts?${params}`, {
    headers: getAuthHeaders(),
  });
  handleResponse(response);
  if (!response.ok) throw new Error("Failed to fetch AI prompts");
  return response.json();
};

export const saveAiPrompt = async (functionId, year, key, enabled, prompt, updatedBy) => {
  const response = await fetch(`${BASE_URL}/save_ai_prompts`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      function_id: functionId,
      year: String(year),
      key,
      enabled,
      prompt,
      updated_by: updatedBy,
    }),
  });
  handleResponse(response);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Failed to save AI prompt");
  }
  return response.json();
};

export const generateInsights = async (functionId, year, week, key) => {
  const response = await fetch(`${BASE_URL}/generate_insights`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      function_id: functionId,
      year: String(year),
      week: String(week),
      key,
    }),
  });
  handleResponse(response);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Failed to generate insights");
  }
  return response.json();
};

export const getInsights = async (functionId, year, week, key) => {
  const params = new URLSearchParams({
    function_id: functionId,
    year: String(year),
    week: String(week),
    key,
  });
  const response = await fetch(`${BASE_URL}/get_insights?${params}`, {
    headers: getAuthHeaders(),
  });
  handleResponse(response);
  if (!response.ok) throw new Error("Failed to fetch insights");
  return response.json();
};
